import React, { useState } from 'react';

import { Button, Step, StepLabel, Stepper, Paper } from '@material-ui/core';
import { FormikProvider, useFormik } from 'formik';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import actionLibraryInstancesApi from 'config/api/actionLibraryInstance/actionLibraryInstance';
import { BulkCreateActionLibraryInstancePayload } from 'config/api/actionLibraryInstance/actionLibraryInstance.types';
import action_library_messages from 'messages/action_library_messages';
import PATHS from 'router/PATHS';

import ActionLibraryInstanceSummaryDialog from './_components/ActionLibraryInstanceSummaryDialog/ActionLibraryInstanceSummaryDialog';
import StepAssetSelection from './_components/Steps/StepAssetSelection';
import StepTemplateSelection from './_components/Steps/StepTemplateSelection';
import StepTimeParameters from './_components/Steps/StepTimeParameters';
import useStyles from './ActionLibraryInstanceForm.styles';
import { ActionLibraryInstanceFormValues } from './ActionLibraryInstanceForm.types';
import actionLibraryInstanceValidationSchema, { step1Schema, step2Schema, step3Schema } from './ActionLibraryInstanceForm.validation';

type Props = {
  initialValues?: ActionLibraryInstanceFormValues;
};

const steps = [
  action_library_messages.wizard.step_select_template,
  action_library_messages.wizard.step_time_parameters,
  action_library_messages.wizard.step_assets,
];

const ActionLibraryInstanceForm: React.FC<Props> = ({ initialValues }) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const history = useHistory();
  const styles = useStyles();
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [showSummaryDialog, setShowSummaryDialog] = useState(false);

  const formik = useFormik<ActionLibraryInstanceFormValues>({
    initialValues: initialValues || {
      templateId: '',
      selectedTemplate: null,
      instanceName: '',
      reportedTo: '',
      deadlineDate: '',
      deadlineTime: '16:00',
      reminderDate: '',
      reminderTime: '16:00',
      specialInstruction: '',
      stepOverrides: [],
      assetIds: '',
      selectedAssets: [],
    },
    validationSchema: actionLibraryInstanceValidationSchema[activeStep],
    onSubmit: async () => {
      // This is called only when submitForm is called manually or on standard submit
      // For wizard, we handle it in handleNext/handleSave
    },
  });

  const { values, validateForm, setTouched, touched } = formik;

  const handleSave = async () => {
    try {
      setLoading(true);

      const template = values.selectedTemplate;
      if (!template) throw new Error(t(action_library_messages.errors.no_template_selected));

      // Construct Adhoc Data
      // We map the template steps, applying overrides
      const adhocSteps = (template.steps || []).map(step => {
        const override = values.stepOverrides.find(o => o.originalStepId === step.id);
        const mandatory = override ? override.mandatory : !!step.mandatory;

        return {
          title: step.title,
          instruction: step.shortDescription, // Assuming shortDescription maps to instruction or description
          mandatory,
          order: step.order,
        };
      });

      // Format dates safely
      const formatDate = (date: any) => {
        if (!date) return '';
        if (date instanceof Date) return date.toISOString().split('T')[0];
        // If it's a moment object or string
        const dateStr = String(date);
        if (dateStr.includes('T')) return dateStr.split('T')[0];
        return dateStr;
      };

      const deadlineDateStr = formatDate(values.deadlineDate);
      const reminderDateStr = formatDate(values.reminderDate);

      const payload: BulkCreateActionLibraryInstancePayload = {
        assetIds: (values.selectedAssets || []).map(asset => Number(asset.id)),

        timeParameters: {
          deadline: `${deadlineDateStr}T${values.deadlineTime}:00.000Z`,
          reminder: `${reminderDateStr}T${values.reminderTime}:00.000Z`,
        },
        adhocData: {
          title: values.instanceName,
          description: template.shortDescription || '',
          specialInstruction: values.specialInstruction,
          steps: adhocSteps,
        },
      };

      await actionLibraryInstancesApi.createBulkActionLibraryInstances(payload);
      enqueueSnackbar(t(action_library_messages.success.instance_created), { variant: 'success' });
      history.push(PATHS.ACTION_LIBRARY_INSTANCE);
    } catch (error: any) {
      // console.error(error);
      const responseData = error.response?.data;

      if (responseData) {
        if (responseData.non_field_errors) {
          responseData.non_field_errors.forEach((err: string) => {
            enqueueSnackbar(err, { variant: 'error' });
          });
        }

        if (responseData.time_parameters) {
          const timeErrors = responseData.time_parameters;
          if (timeErrors.reminder) {
            timeErrors.reminder.forEach((err: string) => {
              enqueueSnackbar(t(action_library_messages.errors.reminder_error_prefix, { error: err }), { variant: 'error' });
            });
            formik.setFieldError('reminderDate', timeErrors.reminder[0]);
            formik.setFieldError('reminderTime', timeErrors.reminder[0]);
          }
          if (timeErrors.deadline) {
            timeErrors.deadline.forEach((err: string) => {
              enqueueSnackbar(t(action_library_messages.errors.deadline_error_prefix, { error: err }), { variant: 'error' });
            });
            formik.setFieldError('deadlineDate', timeErrors.deadline[0]);
            formik.setFieldError('deadlineTime', timeErrors.deadline[0]);
          }
        }
      } else {
        enqueueSnackbar(t(action_library_messages.errors.instance_create_failed), { variant: 'error' });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleNext = async () => {
    // Validate current step
    await validateForm();

    // Check if there are errors for the current step's fields
    let currentStepSchema;
    if (activeStep === 0) currentStepSchema = step1Schema;
    else if (activeStep === 1) currentStepSchema = step2Schema;
    else currentStepSchema = step3Schema;

    try {
      await currentStepSchema.validate(values, { abortEarly: false });
      // If validation passes
      if (activeStep === steps.length - 1) {
        setShowSummaryDialog(true);
      } else {
        setActiveStep(prev => prev + 1);
        // Reset touched for the next step so errors don't show immediately
        setTouched({});
      }
    } catch (err: any) {
      // Set touched for all fields in the current step to show errors
      if (err.inner) {
        const touchedFields: any = {};
        err.inner.forEach((error: any) => {
          touchedFields[error.path] = true;
        });
        setTouched({ ...touched, ...touchedFields });
      }
    }
  };

  const handleBack = () => {
    setActiveStep(prev => prev - 1);
  };

  const handleCloseSummaryDialog = () => {
    setShowSummaryDialog(false);
  };

  const handleConfirmSummary = async () => {
    setShowSummaryDialog(false);
    await handleSave();
  };

  const getStepContent = (step: number) => {
    switch (step) {
      case 0:
        return <StepTemplateSelection />;
      case 1:
        return <StepTimeParameters />;
      case 2:
        return <StepAssetSelection />;
      default:
        return t(action_library_messages.errors.unknown_step);
    }
  };

  const getButtonText = () => {
    if (activeStep === steps.length - 1) {
      return t(action_library_messages.wizard.review);
    }
    return t(action_library_messages.wizard.next);
  };

  return (
    <FormikProvider value={formik}>
      <div className={styles.root}>
        <Stepper activeStep={activeStep} alternativeLabel className={styles.stepper}>
          {steps.map((label, index) => (
            <Step key={index}>
              <StepLabel>{t(label)}</StepLabel>
            </Step>
          ))}
        </Stepper>

        <Paper style={{ padding: 24 }}>
          <div className={styles.content}>{getStepContent(activeStep)}</div>

          <div className={styles.actions}>
            {activeStep > 0 && <Button onClick={handleBack}>{t(action_library_messages.wizard.back)}</Button>}

            {(activeStep > 0 || values.selectedTemplate) && (
              <Button color='primary' disabled={loading} onClick={handleNext} variant='contained'>
                {getButtonText()}
              </Button>
            )}
          </div>
        </Paper>
      </div>

      <ActionLibraryInstanceSummaryDialog
        isSubmitting={loading}
        onClose={handleCloseSummaryDialog}
        onConfirm={handleConfirmSummary}
        open={showSummaryDialog}
        values={values}
      />
    </FormikProvider>
  );
};

export default ActionLibraryInstanceForm;
